import { isDefined, isEmpty } from '@sgme/fp';
import { Tooltip } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { Form } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { FormField } from '@/App/tiles/ForwardAccumulator/parts/RightPanel/common/FormField.tsx';
import {
  useCheckForwardAccumulatorByTradeCapture,
  useForwardAccumulatorTileId,
} from '@/App/tiles/ForwardAccumulator/ProductProvider.tsx';
import { isNotEmpty } from '@/components/sg-design-system/utils/typescript-helpers.ts';
import {
  selectForwardAccumulatorCrossCurrencyData,
  selectForwardAccumulatorFixingReference2Data,
  selectForwardAccumulatorProperty,
} from '@/store/forwardAccumulator/selectors.ts';
import { useAppSelector } from '@/store/hooks.ts';

const FixingReference2 = () => {
  const tileId = useForwardAccumulatorTileId();
  const check = useCheckForwardAccumulatorByTradeCapture();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const { fixingReference2, fixingReference2List, fixingReference2Default } = useAppSelector((state) =>
    selectForwardAccumulatorFixingReference2Data(state, tileId),
  );

  const { isCrossed, crossCurrency } = useAppSelector((state) =>
    selectForwardAccumulatorCrossCurrencyData(state, tileId),
  );
  const currencyPair = useAppSelector((state) => selectForwardAccumulatorProperty(state, tileId, 'currencyPair'));
  const [currency1, currency2] = currencyPair ? currencyPair.split('/') : [undefined, undefined];

  const shouldDisplaySecondFixingReference =
    crossCurrency !== currency2 && crossCurrency !== currency1 && isNotEmpty(crossCurrency);

  const currencyPairDisplayed = shouldDisplaySecondFixingReference ? `${crossCurrency}/${currency2}` : currencyPair;

  const isSameReferenceAsDefault = fixingReference2 === fixingReference2Default;

  const handleFixingReference2 = (event: React.ChangeEvent<HTMLSelectElement>) => {
    sgwtWebAnalytics?.trackEvent('Forward Accumulator', 'Fixing reference 2 changed', event.target.value);

    return check({
      fixingReference2: event.target.value,
    });
  };

  const shouldDisplayFixingReference2 =
    isCrossed && isDefined(crossCurrency) && !isEmpty(fixingReference2List) && shouldDisplaySecondFixingReference;
  if (!shouldDisplayFixingReference2) {
    return null;
  }

  return (
    <FormField
      formattedMessageId="forwardAccumulator.form.fixingReference"
      formattedMessageValues={{ currencyPair: currencyPairDisplayed ?? '' }}
      extra={
        !isSameReferenceAsDefault ? (
          <Tooltip
            position="right"
            contents={
              <FormattedMessage
                id="forwardAccumulator.form.fixingReference.info"
                values={{ defaultFixingReference: fixingReference2Default, br: <br /> }}
              />
            }
          >
            <div className="badge badge-dismissible">
              <em className="icon icon-sm cursor-default line-height-1 text-secondary">info_outline</em>
            </div>
          </Tooltip>
        ) : undefined
      }
    >
      <Form.Select
        value={fixingReference2 ?? fixingReference2Default}
        onChange={handleFixingReference2}
        size="sm"
        className={`${!isSameReferenceAsDefault ? 'text-warning fw-bold' : ''}`}
        data-e2e="fixing-reference2-selector"
      >
        {fixingReference2List?.map((ref) => (
          <option className="text-primary" key={ref} value={ref}>
            {ref}
          </option>
        ))}
      </Form.Select>
    </FormField>
  );
};

export default FixingReference2;
